/**
 * Resolves chat identities — opaque handles (FAME-443), known by JID — to
 * directory profiles. Two batched stages, one hook each:
 *
 *   1. useUserIdsByHandle   handle  -> user id   (costanza)
 *   2. useDirectoryProfiles user id -> profile   (Attensi directory)
 *
 * Call it once near the top of a view with every visible identity and pass
 * `resolvePeer` down, so both stages stay one request each however many rows
 * there are. The decision table lives in peerProfiles.ts.
 */
import { type QueryClient, useQueryClient } from "@tanstack/react-query"
import { toast } from "materia"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import type { PeerProfile } from "../types"
import type { Jid } from "../xmpp/jid"
import { jidLocalPart } from "../xmpp/jid"
import { peerHandles, resolveProfile, userIdsOf } from "./peerProfiles"
import { useDirectoryProfiles } from "./useDirectoryProfiles"
import { useUserIdsByHandle } from "./useUserIdsByHandle"

/**
 * Profiles we already knew when a chat was opened, keyed by handle. A DM
 * peer the picker just handed us (FAME-713) lives here so the new chat shows
 * their name before `resolveHandles` learns the just-minted handle. Its own
 * cache entry, apart from the per-batch resolve keys, so it outlives whichever
 * batch reads it.
 */
const SEED_KEY = ["chat", "handles", "seeded"] as const

/** Remember a handle's profile so resolution can name it right away. */
export function seedPeerProfile(
  queryClient: QueryClient,
  handle: string,
  profile: PeerProfile
): void {
  queryClient.setQueryData<Map<string, PeerProfile>>(SEED_KEY, (prev) =>
    new Map(prev).set(handle, profile)
  )
}

/**
 * Our own identity, resolved outside the directory. The directory lists other
 * people, never the caller, so our own handle would otherwise resolve to the
 * bare user id — the profile comes from CurrentUserProvider instead (FAME-711).
 */
export type SelfProfile = { handle: string; profile: PeerProfile }

/**
 * @param peers        conversation peers, resolved by their JID's localpart
 * @param memberHandles already-bare handles, as `getGroup` reports a group's
 *                      members — resolved in the same batch as the peers
 * @param self         our own handle and profile, short-circuited before the
 *                      directory lookup that omits us
 * @param toastOnFailure false for background callers that must not pop a toast
 *                       or report the failure (one per notification would flood)
 */
export function usePeerProfiles(
  peers: Jid[],
  memberHandles: string[] = [],
  self?: SelfProfile,
  { toastOnFailure = true }: { toastOnFailure?: boolean } = {}
) {
  const { t } = useTranslation()
  const handles = useUserIdsByHandle(peerHandles(peers, memberHandles), {
    report: toastOnFailure,
  })
  const userIdByHandle = handles.userIdByHandle
  const directory = useDirectoryProfiles(userIdsOf(userIdByHandle), {
    report: toastOnFailure,
  })
  const profiles = directory.profiles
  const failed = handles.isError || directory.isError
  // Read, don't subscribe: startDirect seeds before it navigates, so the
  // render that opens the chat already sees the seed.
  const seeded =
    useQueryClient().getQueryData<Map<string, PeerProfile>>(SEED_KEY)
  // Names are still on their way: neither stage failed, and one has not landed.
  // A handle with a seed is already named, so it never waits.
  const isResolving =
    peerHandles(peers, memberHandles).some((handle) => !seeded?.has(handle)) &&
    !failed &&
    (userIdByHandle === undefined ||
      (userIdsOf(userIdByHandle).length > 0 && profiles === undefined))

  // Said once per failure; the slots it leaves read "Unknown" rather than
  // loading.
  useEffect(() => {
    if (failed && toastOnFailure) toast(String(t("chat.PROFILES_FAILED")))
  }, [failed, toastOnFailure, t])

  const resolve = (handle: string): PeerProfile =>
    self && handle === self.handle
      ? self.profile
      : resolveProfile(userIdByHandle, profiles, handle, seeded, failed, {
          resolving: String(t("chat.PROFILE_RESOLVING")),
          failed: String(t("chat.PROFILE_UNKNOWN")),
          someone: String(t("chat.PROFILE_SOMEONE")),
        })

  return {
    isResolving,
    failed,

    resolvePeer: (peer: Jid) => resolve(jidLocalPart(peer)),

    resolveHandle: (handle: string) => resolve(handle),

    /**
     * Stage 1 alone, for callers that must ADDRESS a user rather than show
     * them — removing a group member takes a user id, not a handle.
     */
    resolveUserId: (handle: string): string | null =>
      userIdByHandle?.get(handle) ?? null,
  }
}
